import { useContext, useEffect } from "react"
import { navigate } from "gatsby"
import FirebaseContext from "./context"

/**
 * Custom hook for access control with proper loading state handling
 * 
 * Usage - For authenticated/protected pages:
 * const { isLoading, hasAccess } = useAccessControl({
 *   requireAuth: true,
 *   requireActiveMembership: true,
 *   redirectTo: "/"
 * })
 * 
 * if (isLoading) return <LoadingSpinner />
 * if (!hasAccess) return null
 * 
 * Usage - For sign-out-only pages (login, signup):
 * const { isLoading, hasAccess } = useAccessControl({
 *   requireSignedOut: true,
 *   redirectTo: "/dashboard"
 * })
 * 
 * if (isLoading) return <LoadingSpinner />
 * if (!hasAccess) return null
 * 
 * @param {Object} options - Access control options
 * @param {boolean} [options.requireAuth=false] - Require user to be authenticated
 * @param {boolean} [options.requireActiveMembership=false] - Require active membership
 * @param {boolean} [options.requireSignedOut=false] - Require user to be signed OUT (for login/signup pages)
 * @param {string} [options.redirectTo="/"] - Where to redirect if access denied
 * @param {boolean} [options.replace=true] - Use replace instead of push for navigation
 * @returns {Object} { isLoading, hasAccess }
 */
export function useAccessControl({
  requireAuth = false,
  requireActiveMembership = false,
  requireSignedOut = false,
  redirectTo = "/",
  replace = true,
} = {}) {
  const { user, loading } = useContext(FirebaseContext)

  // Effect handles redirects after loading completes and access is determined
  useEffect(() => {
    // Don't do anything while loading - wait for auth state to resolve
    if (loading) {
      return
    }

    // Now that loading is complete, check access conditions
    let hasAccess = true
    let shouldRedirect = false

    // For sign-out-only pages (login, signup)
    if (requireSignedOut) {
      if (user?.membership?.active === true) {
        hasAccess = false
        shouldRedirect = true
      }
    } 
    // For protected pages (dashboard, member areas)
    else {
      if (requireAuth && !user) {
        hasAccess = false
        shouldRedirect = true
      }

      if (requireActiveMembership && user && user?.membership?.active !== true) {
        hasAccess = false
        shouldRedirect = true
      }
    }

    if (shouldRedirect) {
      navigate(redirectTo, { replace: replace })
    }
  }, [loading, user, requireAuth, requireActiveMembership, requireSignedOut, redirectTo, replace])

  // Return isLoading and hasAccess for component rendering
  return {
    isLoading: loading,
    hasAccess: loading 
      ? true 
      : (requireSignedOut 
          ? user?.membership?.active !== true  // For sign-out pages, hasAccess = not signed in
          : (user !== null && (!requireActiveMembership || user?.membership?.active === true))  // For protected pages
        )
  }
}

export default useAccessControl
